import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollectionForm } from 'forms';
import { DatatableDefault, DatatableDigitalAssets, Modal } from 'components';
import { getCruds, getSource, read } from 'utils';
import { useUserStateValues } from 'states';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactTooltip from 'react-tooltip';

const DatatableCollections = ({ data = null, tenant = null }) => {
  const { t } = useTranslation();
  const userState = useUserStateValues();
  const isCancelled = React.useRef(false);

  const columns = [
    {
      name: t('collection_plural'),
      selector: 'name',
      sortable: true
    },
    {
      name: t('actions'),
      selector: 'actions',
      center: true,
      cell: (row) => (
        <div
          className={'d-flex align-items-center w-100 justify-content-evenly'}>
          <button
            type={'button'}
            className={'btn btn-link d-f-0150'}
            onClick={() => {
              setSelectedCollection(row);
            }}
            data-bs-toggle={'modal'}
            data-bs-target={'#collections-table-datatableEdit'}
            data-tip
            data-for={'edit-row-' + row['@uuid']}>
            <span className={'btn-wrapper--icon me-2'}>
              <FontAwesomeIcon icon={['fas', 'edit']} />
              <ReactTooltip
                id={'edit-row-' + row['@uuid']}
                type={'info'}
                effect={'solid'}>
                <span>{t('edit')}</span>
              </ReactTooltip>
            </span>
          </button>
        </div>
      )
    }
  ];

  // Be careful ! data = row (locked param name)
  const ExpandedComponent = ({ data }) => {
    const [collection, setCollection] = useState(null);
    const settingsDigitalAssets = {
      tableId: 'digitalassets-sub-table',
      title: t('digitalAssetsList'),
      loading: false,
      subdatatable: true,
      removeAction: true,
      datatableParent: 'collection',
      parent: data,
      canReload: false
    };

    useEffect(() => {
      const source = getSource();

      read({
        resourceType: 'digitalAssetsCollection',
        id: data['@uuid'],
        success: {
          callback: (res) => {
            if (isCancelled.current) return;

            if (res.data) {
              setCollection(res.data);
            }
          }
        },
        aOptions: { cancelToken: source.token }
      }).then();

      return () => {
        isCancelled.current = true;
        source.cancel();
        setCollection(null);
      };

      // eslint-disable-next-line
    }, []);

    return (
      <>
        {collection && (
          <DatatableDigitalAssets
            data={collection.digitalAssets ?? null}
            settings={settingsDigitalAssets}
          />
        )}
      </>
    );
  };

  const [selectedCollection, setSelectedCollection] = useState(null);
  const [cruds, setCruds] = useState(null);

  useEffect(() => {
    const newCruds = getCruds({
      resourceType: 'digitalAssetsCollection',
      userState,
      translator: t
    });

    setCruds(newCruds);

    return () => {
      setCruds(null);
    };

    // eslint-disable-next-line
  }, []);

  return (
    cruds && (
      <>
        <DatatableDefault
          columns={columns}
          data={data}
          tenant={tenant}
          settings={{
            tableId: 'collections-table',
            title: t('collection_plural'),
            selectedMessage: {
              singular: t('collection'),
              plural: t('collection_plural')
            },
            expandableRows: true,
            expandableRowsComponent: <ExpandedComponent data={data} />,
            contextActionType: 'merge',
            modals: {
              actions: [
                {
                  label: 'add',
                  modal: (
                    <Modal
                      options={{
                        id: 'collections-table-datatableAdd',
                        icon: {
                          name: 'users'
                        },
                        title: t('addCollection'),
                        useFooter: false
                      }}>
                      <CollectionForm />
                    </Modal>
                  )
                }
              ]
            }
          }}
          pagination={{
            orderBy: 'uploads',
            sortDirection: 'ASC'
          }}
          cruds={cruds}
        />

        <Modal
          options={{
            id: 'collections-table-datatableEdit',
            icon: {
              name: 'edit'
            },
            title:
              t('edit') +
              ' ' +
              (selectedCollection ? selectedCollection.name ?? '' : ''),
            useFooter: false
          }}>
          <CollectionForm entity={selectedCollection} />
        </Modal>
      </>
    )
  );
};

export default DatatableCollections;
